import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { useSearchParams } from "react-router-dom";
import VorgangAkte from "@/components/vorgang/VorgangAkte";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Briefcase, Plus, MagnifyingGlass, Warning, Clock, CheckCircle, X, User, PencilSimple, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatDate, firstEmail } from "@/lib/constants";
import DokumentenmappeTab from "@/components/DokumentenmappeTab";
import OwnerLetterDialog from "@/components/communication/OwnerLetterDialog";
import CasePrefixesDialog from "@/components/communication/CasePrefixesDialog";

const STATUS_LABELS = {
  neu: { label: "Neu", cls: "bg-blue-100 text-blue-800" },
  in_bearbeitung: { label: "In Bearbeitung", cls: "bg-amber-100 text-amber-800" },
  wartet_auf_antwort: { label: "Wartet auf Antwort", cls: "bg-purple-100 text-purple-800" },
  pausiert: { label: "Pausiert", cls: "bg-slate-100 text-slate-700" },
  erledigt: { label: "Erledigt", cls: "bg-emerald-100 text-emerald-800" },
  archiviert: { label: "Archiviert", cls: "bg-slate-200 text-slate-600" },
};
const PRIO_LABELS = {
  niedrig: { label: "Niedrig", cls: "text-slate-500" },
  normal: { label: "Normal", cls: "text-slate-700" },
  hoch: { label: "Hoch", cls: "text-amber-700 font-semibold" },
  dringend: { label: "Dringend", cls: "text-rose-700 font-semibold" },
};
const KATEGORIEN = [
  { v: "versicherungsschaden", l: "Versicherungsschaden" },
  { v: "wasserschaden", l: "Wasserschaden" },
  { v: "brandschaden", l: "Brandschaden" },
  { v: "rechtsangelegenheit", l: "Rechtsangelegenheit" },
  { v: "beschwerde", l: "Beschwerde" },
  { v: "handwerkerbeauftragung", l: "Handwerkerbeauftragung" },
  { v: "angebotseinholung", l: "Angebotseinholung" },
  { v: "behoerdlich", l: "Behördlich" },
  { v: "beschlussnachverfolgung", l: "Beschlussnachverfolgung" },
  { v: "mieterhoehung", l: "Mieterhöhung" },
  { v: "mietminderung", l: "Mietminderung" },
  { v: "vertragsstreit", l: "Vertragsstreit" },
  { v: "klaerung", l: "Klärung" },
  { v: "sonstiges", l: "Sonstiges" },
  { v: "gutachten", l: "Gutachten" },
  { v: "vermittlung", l: "Vermittlung" },
  { v: "betriebskostenabrechnung", l: "Betriebskostenabrechnung" },
];
const AKTIVITAETEN_LABELS = {
  note: "Notiz",
  letter_created: "Schreiben erstellt",
  letter_sent: "Schreiben versendet",
  email_sent: "E-Mail versendet",
  email_received: "E-Mail empfangen",
  call_note: "Telefonnotiz",
  task_created: "Aufgabe angelegt",
  deadline_set: "Frist gesetzt",
  status_changed: "Status geändert",
  document_uploaded: "Dokument hochgeladen",
  participant_added: "Beteiligter hinzugefügt",
  reminder_set: "Wiedervorlage",
};

const ROLLEN = [
  { v: "mieter", l: "Mieter" },
  { v: "eigentuemer", l: "Eigentümer" },
  { v: "eigentuemergemeinschaft", l: "Eigentümergemeinschaft" },
  { v: "versicherung", l: "Versicherung" },
  { v: "handwerker", l: "Handwerker" },
  { v: "rechtsanwalt", l: "Rechtsanwalt" },
  { v: "gemeinde", l: "Gemeinde / Behörde" },
  { v: "energieversorger", l: "Energieversorger" },
  { v: "gutachter", l: "Gutachter" },
  { v: "hausmeister", l: "Hausmeister" },
  { v: "verwalter", l: "Verwalter" },
  { v: "sonstiges", l: "Sonstiges" },
];

export default function Vorgaenge() {
  const [prefixOpen, setPrefixOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [filterStatus, setFilterStatus] = useState("");
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [searchParams] = useSearchParams();
  const [openDetailId, setOpenDetailId] = useState(searchParams.get("id"));

  const load = async () => {
    const params = {};
    if (filterStatus) params.status = filterStatus;
    if (search) params.search = search;
    const { data } = await api.get("/vorgaenge", { params });
    setItems(data || []);
  };
  const loadSummary = async () => {
    try { const { data } = await api.get("/vorgaenge/dashboard/summary"); setSummary(data); }
    catch { setSummary({}); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterStatus]);
  useEffect(() => { const h = setTimeout(load, 300); return () => clearTimeout(h); /* eslint-disable-next-line */ }, [search]);
  useEffect(() => { loadSummary(); }, []);

  return (
    <div className="space-y-5" data-testid="vorgaenge-page">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
          <Briefcase size={24} className="text-slate-600" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Vorgänge</h1>
          <p className="text-sm text-slate-500 mt-0.5">Digitale Akten für Sachverhalte mit Fristen, Beteiligten und Historie.</p>
        </div>
        <Button variant="outline" onClick={() => setPrefixOpen(true)} data-testid="vorgang-prefixes-btn">Aktenzeichen-Kürzel</Button>
        <CasePrefixesDialog open={prefixOpen} onOpenChange={setPrefixOpen} labels={Object.fromEntries(KATEGORIEN.map((k) => [k.v, k.l]))} />
        <Button className="bg-slate-900 hover:bg-slate-800 gap-2" onClick={() => setCreating(true)} data-testid="vorgang-new-btn">
          <Plus size={16} /> Neuer Vorgang
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-5 gap-3">
        <Kpi label="Offen" value={summary.open || 0} />
        <Kpi label="Überfällig" value={summary.overdue || 0} accent="rose" icon={<Warning size={14} />} />
        <Kpi label="Wiedervorlage heute" value={summary.reminders_today || 0} accent="amber" icon={<Clock size={14} />} />
        <Kpi label="Wartet auf Antwort" value={summary.waiting_reply || 0} accent="blue" />
        <Kpi label="Mir zugewiesen" value={summary.assigned_to_me || 0} accent="emerald" icon={<CheckCircle size={14} />} />
      </div>

      {/* Filter-Bar */}
      <div className="bg-white border border-slate-200 rounded-md p-3 flex items-center gap-3 flex-wrap">
        <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-52 h-9" data-testid="vorgang-status-filter"><SelectValue placeholder="Status: Alle" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Alle</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 max-w-[400px]">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="h-9 pl-8 text-sm" placeholder="Titel, Vorgangsnummer, Beschreibung…"
            value={search} onChange={(e) => setSearch(e.target.value)} data-testid="vorgang-search" />
        </div>
      </div>

      {/* Liste */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr>
              <Th>Vorgangs-Nr.</Th><Th>Titel</Th><Th>Kategorie</Th><Th>Priorität</Th><Th>Status</Th><Th>Frist</Th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-slate-500 text-sm">Keine Vorgänge gefunden.</td></tr>}
            {items.map((v) => {
              const s = STATUS_LABELS[v.status] || STATUS_LABELS.neu;
              const p = PRIO_LABELS[v.priority] || PRIO_LABELS.normal;
              const overdue = v.due_date && v.due_date < new Date().toISOString().slice(0, 10) && v.status !== "erledigt";
              return (
                <tr key={v.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => setOpenDetailId(v.id)} data-testid={`vorgang-row-${v.id}`}>
                  <Td className="font-mono text-xs">{v.case_number}</Td>
                  <Td className="font-medium">{v.title}</Td>
                  <Td className="text-xs text-slate-600">{KATEGORIEN.find(k => k.v === v.category)?.l || v.category}</Td>
                  <Td><span className={`text-xs ${p.cls}`}>{p.label}</span></Td>
                  <Td><span className={`text-[10px] px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span></Td>
                  <Td className={`tabular-nums text-xs ${overdue ? "text-rose-700 font-semibold" : "text-slate-600"}`}>
                    {v.due_date ? formatDate(v.due_date) : "—"}
                    {overdue && " ⚠"}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {creating && <NewVorgangDialog onClose={() => setCreating(false)} onSaved={(v) => { setCreating(false); load(); loadSummary(); setOpenDetailId(v.id); }} />}
      {openDetailId && <VorgangDetailDialog id={openDetailId} onClose={() => setOpenDetailId(null)} onChanged={() => { load(); loadSummary(); }} />}
    </div>
  );
}

function Kpi({ label, value, accent, icon }) {
  const cls = accent === "rose" ? "text-rose-700" : accent === "amber" ? "text-amber-700"
            : accent === "blue" ? "text-blue-700" : accent === "emerald" ? "text-emerald-700" : "text-slate-900";
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">{icon}{label}</div>
      <div className={`text-2xl font-semibold tabular-nums mt-1 ${cls}`}>{value}</div>
    </div>
  );
}

function NewVorgangDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({
    title: "", category: "klaerung", priority: "normal", description: "",
    due_date: "", tenancy_id: "", property_id: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.title.trim()) { toast.error("Titel ist Pflicht"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/vorgaenge", { ...form, tenancy_id: form.tenancy_id || null, property_id: form.property_id || null });
      toast.success(`Vorgang ${data.case_number} angelegt`);
      onSaved(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler beim Anlegen"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="new-vorgang-dialog">
        <DialogHeader>
          <DialogTitle>Neuen Vorgang anlegen</DialogTitle>
          <DialogDescription>Digitale Akte mit Fristen, Beteiligten und Timeline.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Titel *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                   placeholder="z. B. Wasserschaden Küche" data-testid="vorgang-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Kategorie</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="vorgang-category"><SelectValue /></SelectTrigger>
                <SelectContent>{KATEGORIEN.map(k => <SelectItem key={k.v} value={k.v}>{k.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priorität</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="vorgang-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PRIO_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Beschreibung</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="Kurze Beschreibung des Sachverhalts" />
          </div>
          <div>
            <Label>Frist (optional)</Label>
            <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                   data-testid="vorgang-due-date" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Abbrechen</Button>
          <Button className="bg-slate-900 hover:bg-slate-800" onClick={submit} disabled={busy} data-testid="vorgang-create-submit">
            {busy ? "Lege an…" : "Vorgang anlegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VorgangDetailDialog({ id, onClose, onChanged }) {
  const [v, setV] = useState(null);
  const [newNote, setNewNote] = useState("");
  // 2026-02 — Vor-Auswahl der Empfänger, wenn ein Vorgang mehrere Beteiligte/Ansprechpartner hat
  const [recipientPicker, setRecipientPicker] = useState({ open: false, candidates: [], selected: new Set() });
  const [ownerLetterOpen, setOwnerLetterOpen] = useState(false);
  const [akte, setAkte] = useState(null);
  const load = async () => {
    try {
      const { data } = await api.get(`/vorgaenge/${id}/akte`);
      setAkte(data); setV(data.vorgang);
    } catch (e) { toast.error(e?.response?.data?.detail || "Vorgang konnte nicht geladen werden"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const updateStatus = async (newStatus) => {
    try {
      await api.patch(`/vorgaenge/${id}`, { status: newStatus });
      toast.success("Status aktualisiert");
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };
  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post(`/vorgaenge/${id}/aktivitaeten`, { kind: "note", text: newNote });
      setNewNote("");
      load();
    } catch (e) { toast.error("Notiz konnte nicht angelegt werden"); }
  };

  if (!v) return null;
  const s = STATUS_LABELS[v.status] || STATUS_LABELS.neu;

  return (
    <>
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col p-0" data-testid="vorgang-detail-dialog">
        <DialogHeader className="px-5 pt-4 pb-3 border-b border-slate-200">
          <div className="flex items-center gap-2 text-sm text-slate-900 font-mono font-semibold" data-testid="vorgang-case-number">Az. {v.case_number}{v.unser_zeichen && v.unser_zeichen !== v.case_number ? <span className="text-xs text-slate-500 font-normal">· {v.unser_zeichen}</span> : null}</div>
          <DialogTitle className="text-lg">{v.title}</DialogTitle>
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-xs px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>
            <span className={`text-xs ${(PRIO_LABELS[v.priority] || {}).cls}`}>{(PRIO_LABELS[v.priority] || {}).label}</span>
            <span className="text-xs text-slate-500">· {KATEGORIEN.find(k => k.v === v.category)?.l}</span>
            {v.due_date && <span className="text-xs text-slate-500 ml-auto">Frist: {formatDate(v.due_date)}</span>}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto grid grid-cols-1 md:grid-cols-3 gap-0 min-h-0">
          <div className="md:col-span-2 md:border-r border-slate-200 p-5 overflow-y-auto">
            {v.description && <div className="mb-3 text-sm text-slate-700" data-testid="vorgang-description">{v.description}</div>}
            {akte && <VorgangAkte akte={akte} onChanged={load} activityTab={
              <div className="space-y-3">
                <div>
                  <Textarea rows={2} placeholder="Notiz oder Kommentar hinzufügen…" value={newNote} onChange={(e) => setNewNote(e.target.value)} data-testid="vorgang-new-note" />
                  <div className="flex justify-end mt-1"><Button size="sm" onClick={addNote} disabled={!newNote.trim()} data-testid="vorgang-note-submit">Notiz speichern</Button></div>
                </div>
                {(v.activity || []).slice().reverse().map((a) => (
                  <div key={a.id} className="text-sm border-l-2 border-slate-200 pl-3 py-1" data-testid={`activity-${a.id}`}>
                    <div className="text-xs font-semibold text-slate-700">{AKTIVITAETEN_LABELS[a.kind] || a.kind}</div>
                    {a.text && <div className="text-sm text-slate-700">{a.text}</div>}
                    <div className="text-[10px] text-slate-400 mt-0.5">{formatDate(a.created_at)} · {a.user_name || "System"}</div>
                  </div>
                ))}
                <details className="text-xs"><summary className="cursor-pointer text-slate-500">Versand-Mappe (Postausgang)</summary><DokumentenmappeTab vorgangId={v.id} /></details>
              </div>} />}
          </div>

          {/* Beteiligte + Aktionen */}
          <div className="p-5 space-y-4 bg-slate-50">
            <div>
              <h3 className="text-xs font-semibold uppercase text-slate-600 mb-2">Status ändern</h3>
              <Select value={v.status} onValueChange={updateStatus}>
                <SelectTrigger data-testid="vorgang-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <BeteiligtenPanel vorgang={v} onChanged={load} />
          </div>
        </div>

        <DialogFooter className="mx-0 mb-0 px-5 py-3 border-t border-slate-200 bg-white flex flex-wrap gap-2 sm:gap-3 justify-end">
          <Button
            variant="outline"
            data-testid="vorgang-schreiben-btn"
            onClick={async () => {
              // 2026-02 — Empfänger-Vorabprüfung: Wenn der Vorgang mehr als
              // einen möglichen Empfänger hat, zuerst Auswahl-Dialog zeigen.
              const ctx = { vorgang_id: v.id };
              if (v.tenancy_id) ctx.tenancy_id = v.tenancy_id;
              if (v.property_id) ctx.property_id = v.property_id;
              if (v.contact_id) ctx.contact_id = v.contact_id;
              try {
                const { data } = await api.get(`/vorgaenge/${v.id}/recipients`);
                const cands = Array.isArray(data) ? data : [];
                if (cands.length > 1) {
                  const pre = new Set(cands.filter((c) => c.default).map((c) => c.key));
                  setRecipientPicker({ open: true, candidates: cands, selected: pre });
                  return;
                }
              } catch { /* Fallback: einfach Composer öffnen */ }
              window.dispatchEvent(new CustomEvent("domivus:open-composer", { detail: { context: ctx } }));
              onClose();
            }}
          >
            ✎ Schreiben verfassen
          </Button>
          {(v?.property_id || v?.tenancy_id) && (
            <Button variant="outline" onClick={() => setOwnerLetterOpen(true)} data-testid="vorgang-owner-letter-btn">An Eigentümer schreiben</Button>
          )}
          <Button variant="outline" onClick={onClose}>Schließen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {v && <OwnerLetterDialog open={ownerLetterOpen} onOpenChange={setOwnerLetterOpen} target={{ vorgang_id: v.id }} />}
    {/* 2026-02 — Empfänger-Vorabauswahl aus Vorgang */}
    {recipientPicker.open && v && (
      <Dialog open={recipientPicker.open} onOpenChange={(o) => !o && setRecipientPicker({ open: false, candidates: [], selected: new Set() })}>
        <DialogContent className="max-w-lg" data-testid="vorgang-recipient-picker">
          <DialogHeader>
            <DialogTitle>Wer soll angeschrieben werden?</DialogTitle>
            <DialogDescription>
              Der Vorgang „{v.title}“ hat mehrere mögliche Empfänger. Wählen Sie einen oder mehrere aus.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 max-h-[50vh] overflow-y-auto py-2">
            {recipientPicker.candidates.map((c) => {
              const checked = recipientPicker.selected.has(c.key);
              return (
                <label
                  key={c.key}
                  className={`flex items-start gap-2 p-2 rounded border cursor-pointer transition ${checked ? "bg-emerald-50 border-emerald-300" : "bg-white border-slate-200 hover:bg-slate-50"}`}
                  data-testid={`vorgang-picker-${c.key}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      setRecipientPicker((s) => {
                        const nxt = new Set(s.selected);
                        if (e.target.checked) nxt.add(c.key); else nxt.delete(c.key);
                        return { ...s, selected: nxt };
                      });
                    }}
                    className="mt-0.5 accent-emerald-600 cursor-pointer"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900">{c.label}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">{c.role || c.type}</div>
                  </div>
                </label>
              );
            })}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setRecipientPicker({ open: false, candidates: [], selected: new Set() })}>
              Abbrechen
            </Button>
            <Button
              onClick={() => {
                const selectedKeys = Array.from(recipientPicker.selected);
                if (selectedKeys.length === 0) {
                  toast.error("Bitte mindestens einen Empfänger wählen");
                  return;
                }
                const ctx = { vorgang_id: v.id, preselected_recipient_keys: selectedKeys };
                if (v.tenancy_id) ctx.tenancy_id = v.tenancy_id;
                if (v.property_id) ctx.property_id = v.property_id;
                if (v.contact_id) ctx.contact_id = v.contact_id;
                setRecipientPicker({ open: false, candidates: [], selected: new Set() });
                window.dispatchEvent(new CustomEvent("domivus:open-composer", { detail: { context: ctx } }));
                onClose();
              }}
              className="bg-slate-900 hover:bg-slate-800 gap-1"
              data-testid="vorgang-picker-confirm"
            >
              Weiter zum Schreiben ({recipientPicker.selected.size})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )}
  </>
  );
}
function Td({ children, className = "" }) { return <td className={`px-3 py-3 ${className}`}>{children}</td>; }
function Th({ children }) { return <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">{children}</th>; }

/** VM-2b — Beteiligten-Verwaltung mit Kontakt-Autocomplete */
function BeteiligtenPanel({ vorgang, onChanged }) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [role, setRole] = useState("sonstiges");
  const [ihrZeichen, setIhrZeichen] = useState("");
  const [contactsCache, setContactsCache] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const debounceRef = useRef(null);

  // Kontakte für bestehende Beteiligte laden (Namen anzeigen)
  useEffect(() => {
    (async () => {
      const ids = (vorgang.participants || []).map((p) => p.contact_id).filter((id) => id && !contactsCache[id]);
      if (!ids.length) return;
      const fetched = {};
      for (const id of ids) {
        try {
          const { data } = await api.get(`/contacts/${id}`);
          fetched[id] = data;
        } catch { fetched[id] = null; }
      }
      setContactsCache((prev) => ({ ...prev, ...fetched }));
    })();
    // eslint-disable-next-line
  }, [vorgang.participants?.length]);

  const searchContacts = (q) => {
    setQuery(q);
    setSelectedContact(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.length < 2) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts", { params: { q, limit: 10 } });
        setSuggestions(data || []);
      } catch { setSuggestions([]); }
    }, 200);
  };

  const addParticipant = async () => {
    if (!selectedContact) { toast.error("Bitte Kontakt auswählen"); return; }
    try {
      await api.post(`/vorgaenge/${vorgang.id}/beteiligte`, {
        contact_id: selectedContact.id,
        role,
        ihr_zeichen: ihrZeichen || undefined,
      });
      toast.success("Beteiligter hinzugefügt");
      setContactsCache((prev) => ({ ...prev, [selectedContact.id]: selectedContact }));
      setSelectedContact(null); setQuery(""); setIhrZeichen(""); setSuggestions([]);
      setAddOpen(false);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler beim Hinzufügen"); }
  };

  const removeParticipant = async (pid) => {
    if (!await window._domivusConfirm("Diesen Beteiligten wirklich entfernen?")) return;
    try {
      await api.delete(`/vorgaenge/${vorgang.id}/beteiligte/${pid}`);
      toast.success("Beteiligter entfernt");
      onChanged?.();
    } catch { toast.error("Fehler"); }
  };

  // 2026-02 — Beteiligten inline editieren (Rolle + Aktenzeichen + Notiz)
  const [editingId, setEditingId] = useState(null);
  const [editRole, setEditRole] = useState("sonstiges");
  const [editIhrZeichen, setEditIhrZeichen] = useState("");
  const [editNote, setEditNote] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditRole(p.role || "sonstiges");
    setEditIhrZeichen(p.ihr_zeichen || "");
    setEditNote(p.note || "");
  };
  const cancelEdit = () => {
    setEditingId(null); setEditIhrZeichen(""); setEditNote(""); setEditRole("sonstiges");
  };
  const saveEdit = async (pid) => {
    setEditSaving(true);
    try {
      await api.patch(`/vorgaenge/${vorgang.id}/beteiligte/${pid}`, {
        role: editRole,
        ihr_zeichen: editIhrZeichen || null,
        note: editNote || null,
      });
      toast.success("Beteiligter aktualisiert");
      cancelEdit();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setEditSaving(false); }
  };

  return (
    <div data-testid="beteiligte-panel">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase text-slate-600">
          Beteiligte ({(vorgang.participants || []).length})
        </h3>
        <button
          onClick={() => setAddOpen((v) => !v)}
          className="text-xs text-slate-700 hover:text-slate-900 font-semibold flex items-center gap-1"
          data-testid="beteiligter-add-toggle">
          <Plus size={12} /> Hinzufügen
        </button>
      </div>

      {addOpen && (
        <div className="bg-white border border-slate-200 rounded p-3 mb-3 space-y-2" data-testid="beteiligter-add-form">
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Kontakt suchen</Label>
            <div className="relative">
              <Input
                value={query}
                onChange={(e) => searchContacts(e.target.value)}
                placeholder="Name, Firma, E-Mail…"
                className="h-8 text-xs"
                data-testid="beteiligter-search-input"
              />
              {suggestions.length > 0 && !selectedContact && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded shadow-lg max-h-52 overflow-y-auto z-50" data-testid="beteiligter-suggestions">
                  {suggestions.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => { setSelectedContact(c); setQuery(c.display_name || ""); setSuggestions([]); }}
                      className="w-full text-left px-3 py-1.5 hover:bg-slate-100 text-sm border-b border-slate-100 last:border-0"
                      data-testid={`beteiligter-suggestion-${c.id}`}>
                      <div className="font-medium text-slate-800">{c.display_name || c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim()}</div>
                      {firstEmail(c.emails) && <div className="text-[11px] text-slate-500">{firstEmail(c.emails)}</div>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {selectedContact && (
            <div className="text-xs bg-emerald-50 border border-emerald-200 rounded px-2 py-1 flex items-center justify-between" data-testid="beteiligter-selected">
              <span className="text-emerald-900"><User size={12} className="inline mr-1" /> {selectedContact.display_name}</span>
              <button onClick={() => { setSelectedContact(null); setQuery(""); }} className="text-emerald-700 hover:text-emerald-900">
                <X size={12} />
              </button>
            </div>
          )}

          <div>
            <Label className="text-[10px] uppercase tracking-wider">Rolle</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="h-8 text-xs" data-testid="beteiligter-role"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLLEN.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Ihr Zeichen (optional)</Label>
            <Input value={ihrZeichen} onChange={(e) => setIhrZeichen(e.target.value)} className="h-8 text-xs"
              placeholder="z.B. Schaden-Nr. 12345"
              data-testid="beteiligter-ihr-zeichen" />
          </div>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => { setAddOpen(false); setSelectedContact(null); setQuery(""); }}>
              Abbrechen
            </Button>
            <Button size="sm" onClick={addParticipant} disabled={!selectedContact} data-testid="beteiligter-add-submit">
              Hinzufügen
            </Button>
          </div>
        </div>
      )}

      {(vorgang.participants || []).length === 0 ? (
        <div className="text-xs text-slate-400 italic">Noch keine Beteiligten.</div>
      ) : (
        <div className="space-y-1">
          {(vorgang.participants || []).map((p) => {
            const c = contactsCache[p.contact_id];
            const roleObj = ROLLEN.find((r) => r.v === p.role);
            const isEditing = editingId === p.id;
            return (
              <div key={p.id} className="text-sm bg-white border border-slate-200 rounded px-2 py-1.5 group" data-testid={`beteiligter-row-${p.id}`}>
                {isEditing ? (
                  <div className="space-y-1.5" data-testid={`beteiligter-edit-${p.id}`}>
                    <div className="font-medium text-slate-800 text-sm truncate">
                      {c ? (c.display_name || c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim()) : `Kontakt ${p.contact_id.slice(0, 8)}…`}
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider">Rolle</Label>
                      <Select value={editRole} onValueChange={setEditRole}>
                        <SelectTrigger className="h-7 text-xs" data-testid={`beteiligter-edit-role-${p.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{ROLLEN.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider">Ihr Zeichen / Az.</Label>
                      <Input value={editIhrZeichen} onChange={(e) => setEditIhrZeichen(e.target.value)} className="h-7 text-xs"
                        placeholder="z.B. Schaden-Nr. 12345"
                        data-testid={`beteiligter-edit-zeichen-${p.id}`} />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider">Notiz (intern)</Label>
                      <Input value={editNote} onChange={(e) => setEditNote(e.target.value)} className="h-7 text-xs"
                        placeholder="freie Notiz"
                        data-testid={`beteiligter-edit-note-${p.id}`} />
                    </div>
                    <div className="flex gap-1 justify-end pt-1">
                      <Button size="sm" variant="ghost" onClick={cancelEdit} className="h-6 text-[11px] px-2" data-testid={`beteiligter-edit-cancel-${p.id}`}>
                        Abbrechen
                      </Button>
                      <Button size="sm" onClick={() => saveEdit(p.id)} disabled={editSaving} className="h-6 text-[11px] px-2 gap-1" data-testid={`beteiligter-edit-save-${p.id}`}>
                        <Check size={11} /> Speichern
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-800 text-sm truncate">
                        {c ? (c.display_name || c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim()) : `Kontakt ${p.contact_id.slice(0, 8)}…`}
                      </div>
                      <div className="text-[10px] text-slate-500 flex items-center gap-1.5 flex-wrap">
                        <span className="px-1.5 py-0.5 rounded bg-slate-100 uppercase tracking-wider font-semibold">{roleObj?.l || p.role}</span>
                        {p.ihr_zeichen && <span className="font-mono">Ihr Z.: {p.ihr_zeichen}</span>}
                        {p.note && <span className="italic text-slate-400 truncate">„{p.note}“</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => startEdit(p)}
                        className="p-1 rounded hover:bg-slate-100 text-slate-600"
                        title="Bearbeiten"
                        data-testid={`beteiligter-edit-${p.id}`}>
                        <PencilSimple size={12} />
                      </button>
                      <button
                        onClick={() => removeParticipant(p.id)}
                        className="p-1 rounded hover:bg-rose-100 text-rose-600"
                        title="Entfernen"
                        data-testid={`beteiligter-remove-${p.id}`}>
                        <X size={12} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
